import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// DropboxIoRules - the arithmetic and the wire format of sending a backup-sized file to Dropbox.
//
// ⚠ IT HAS NO HABITAT, AND THAT IS WHY IT IS A SEPARATE FILE FROM `AppDropboxUpload`. The Bridge
// suite cross-checks both halves of this wire and therefore IMPORTS this file, so a combined
// rules-and-transport module would drag `XMLHttpRequest` and `window` into a Node typecheck: it
// passes under tsx and fails under tsc, which is the exact trap this estate has hit before.
//
// ==================================================================================================
// !! ⛔ ALWAYS AN UPLOAD SESSION, EVEN FOR A SMALL FILE - AND THAT IS THE LESSON FROM DRIVE
// ==================================================================================================
//
// `files/upload` takes a whole file in one request and refuses above 150 MB. `ImportIoRules`' header
// records what that shape cost on Drive: "IT WORKS IN TESTING - a developer's trimmed archive goes
// through, and the first real subscriber's does not." A size-dependent branch means the path that
// runs for real is the path that was never exercised.
//
// ⭐ SO THERE IS ONE PATH: start, append, finish. Measured against the live API 2026-09-23 with a
// 1000-byte start and a 500-byte append - both accepted - so a session is not merely allowed for a
// small file, it is ordinary. ⚠ That also disposed of the "every chunk must be a multiple of 4 MB"
// rule, which applies to CONCURRENT sessions and not to this sequential one.
//
// ==================================================================================================
// !! ⚠ THE OFFSET IS THE WHOLE CORRECTNESS ARGUMENT
// ==================================================================================================
//
// ⛔ DROPBOX TRUSTS THE OFFSET WE SEND. An append with a wrong one is refused with
// `incorrect_offset` naming the expected value, which is the good case - but the offset is also how
// a resumed upload would find its place, so it is computed from bytes ACTUALLY SENT and never from
// a chunk counter. This is `ImportIoRules.NextStart`'s lesson in the other direction: on Drive the
// server states the truth and the client must read it; here the client states it and must be right.
//
// ⚠ AND `finish` SENDS NO BYTES. Every chunk goes up through `append_v2` and `finish` closes the
// session at the total. Folding the last chunk into `finish` works and saves one small request, at
// the cost of a second place where an offset means something slightly different - which is exactly
// the shape of the off-by-one `RangeHeader` exists to prevent.

export class DropboxIoRules {
    public static readonly Content: string = "https://content.dropboxapi.com/2/";

    /**
     * 8 MB.
     *
     * ⚠ NOT A DROPBOX REQUIREMENT - measured, see the header. It is a phone-connection judgement:
     * large enough that a 40 MB database is five round trips, small enough that one lost request
     * does not cost minutes. ⚠ `XMLHttpRequest.upload` reports progress WITHIN a chunk, so this
     * does not decide how often the bar moves.
     */
    public static readonly ChunkBytes: number = 8 * 1024 * 1024;

    /**
     * What the uploaded database is called.
     *
     * ⛔ A FIXED NAME, NEVER A TIMESTAMPED ONE, AND THE REASON IS NOT TIDINESS - carried over from
     * `ImportIoRules.UploadName` with its reasoning intact. This file is every message the
     * subscriber has ever sent or received, in the clear. A name carrying the date would leave a
     * SECOND complete plaintext archive on every re-import, then a third, none ever cleaned up.
     *
     * ⚠ AND THE SERVER HAS TO FIND IT: `ImportPullRules.Wanted` is the same string, and a Bridge
     * test asserts it. A disagreement has no symptom at all - the browser uploads and reports
     * success, the service lists a folder in which nothing matches, and the import never happens.
     */
    public static readonly UploadName: string = "wacommander-msgstore.db";

    /** ⚠ THE SERVICE NAMES THE PATH IN ITS TICKET; this is the fallback when it names none. */
    public static readonly UploadPath: string = "/wacommander-msgstore.db";

    public static StartUrl = (): string => {
        return DropboxIoRules.Content + "files/upload_session/start";
    };

    public static AppendUrl = (): string => {
        return DropboxIoRules.Content + "files/upload_session/append_v2";
    };

    public static FinishUrl = (): string => {
        return DropboxIoRules.Content + "files/upload_session/finish";
    };

    /**
     * ⚠ `close: false` KEEPS THE SESSION OPEN. Sending true on the start of a multi-chunk upload
     * closes it after the first chunk, and every later append is refused as `closed` - a failure
     * that only appears on files big enough to need a second chunk.
     *
     * DOCUMENTED EXCEPTION: composes a constant.
     * ERROR-SUPPRESSED-JUSTIFIED: pure helper, called only from a guarded method.
     */
    public static StartArg = (): string => {
        return JSON.stringify({ close: false });
    };

    public static AppendArg = (SessionId: string, Offset: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Id: string = nz(SessionId).trim();
            const At: number = Math.floor(Number(Offset));
            if (Id.length === 0 || isNaN(At) === true || At < 0) { return ""; }
            return JSON.stringify({ cursor: { session_id: Id, offset: At }, close: false });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "DropboxIoRules.AppendArg");
            return "";
        }
    };

    /**
     * ⛔ `mode: "overwrite"` AND `autorename: false`, FOR `AppDriveSource.PutBeside`'S REASON.
     * Dropbox's `add` renames a clashing upload to `wacommander-msgstore (1).db`, so a second import
     * would leave a file the service never looks for while the subscriber is told it worked.
     *
     * ⚠ `mute: true` - this arrives as one large write and there is no reason to ring every device
     * the subscriber owns over a file they will never open.
     */
    public static FinishArg = (SessionId: string, Offset: number, Path: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Id: string = nz(SessionId).trim();
            const Where: string = nz(Path).trim();
            const At: number = Math.floor(Number(Offset));
            if (Id.length === 0 || Where.length === 0) { return ""; }
            if (isNaN(At) === true || At < 0) { return ""; }
            return JSON.stringify({
                cursor: { session_id: Id, offset: At },
                commit: { path: Where, mode: "overwrite", autorename: false, mute: true },
            });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "DropboxIoRules.FinishArg");
            return "";
        }
    };

    /**
     * The session id out of a `start` answer, or "".
     *
     * ⛔ AN ABSENT ID IS NOT SOMETHING TO RETRY BLINDLY - without it every later chunk would address
     * no session at all. `ImportIoRules.OnSession` recorded the same rule about Drive's `Location`.
     */
    public static SessionIdIn = (Text: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Raw: Record<string, unknown> = JSON.parse(nz(Text)) as Record<string, unknown>;
            if (Raw === null || typeof Raw !== "object") { return ""; }
            return nz(Raw.session_id as string).trim();
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: an unreadable answer is returned as "no session", which
            // the caller turns into a sentence for the subscriber. Reporting it would flip the gate
            // and blank the page they are signing up on.
            return "";
        }
    };

    /** How many bytes to send starting at `Start`. The last chunk is whatever is left. */
    public static ChunkAt = (Start: number, Total: number): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            const S: number = Number(Start);
            const T: number = Number(Total);
            if (isNaN(S) === true || isNaN(T) === true) { return 0; }
            if (S < 0 || S >= T) { return 0; }
            const Left: number = T - S;
            if (Left < DropboxIoRules.ChunkBytes) { return Left; }
            return DropboxIoRules.ChunkBytes;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "DropboxIoRules.ChunkAt");
            return 0;
        }
    };

    /** How far along, 0-100, for a progress line. Never above 100 and never NaN. */
    public static Percent = (Done: number, Total: number): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            const D: number = Number(Done);
            const T: number = Number(Total);
            if (isNaN(D) === true || isNaN(T) === true || T <= 0) { return 0; }
            if (D <= 0) { return 0; }
            if (D >= T) { return 100; }
            return Math.floor((D / T) * 100);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "DropboxIoRules.Percent");
            return 0;
        }
    };

    /**
     * What a failed chunk means, in a sentence.
     *
     * ⚠ 401 IS THE ONE WORTH NAMING. The ticket lasts about four hours and an upload on a poor
     * connection can be long, but not that long - so a 401 here almost always means the sign-up sat
     * open overnight, and "start this step again" is exactly right and exactly not obvious.
     *
     * DOCUMENTED EXCEPTION: builds a sentence from values in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: pure helper, called only from a guarded method.
     */
    public static Why = (Status: number, Text: string): string => {
        if (Status === 0) {
            return "The connection dropped while sending your history. Nothing was changed - try"
                + " again when you have a steadier signal.";
        }
        if (Status === 401) {
            return "This step has been open too long, so Dropbox stopped accepting the upload."
                + " Start this step again - nothing was changed.";
        }
        if (Status === 507 || nz(Text).indexOf("insufficient_space") >= 0) {
            return "Your Dropbox ran out of room part way through, so your history was not saved."
                + " Clear some space in Dropbox and try this step again.";
        }
        return "Dropbox stopped the upload (" + String(Status) + "). Nothing was changed - you can"
            + " try again.";
    };
}
